import { useState } from "react";
import { UserRound, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Label } from "@/shared/ui/label";
import {
  CharacterPickerDialog,
  type CharacterPick,
} from "@/entities/character/ui/CharacterPickerDialog";
import type { FormState } from "../../model/videoCreateTypes";

/**
 * ★真人人物在这一舱**照常上列**(0914 SEEDANCE 真人出镜 P0 车 D):此前(2026-08-13 拍板)
 * 真人的定妆图送进视频模型必被肖像合规拒,判据是「真人 + 有定妆图」就不列。现在真人出镜走
 * vapeur 通道单独送真人线(见 api A4 + ai vapeur_seedance provider),判据改钉服务端认证
 * 状态机 —— 真人是否可选交给 `CharacterPickerDialog` 自己的三态渲染(选得中/置灰+原因),
 * 这里不再传 `filter` 硬性排除:硬排除会让「认证没走完的真人」整条从列表消失,用户看不到
 * 「有这个人,但现在为什么不能选」(D4)。
 */
const PICKER_EMPTY_HINT = "人物库还是空的 —— 去「资产 · 人物」建一位再回来。";

/**
 * cast=real 落地时的空态(D2,2026-09-14):这条流是从「真人出镜」卡专程来的,
 * 库里没有真人人物就该直接指一条路建一个,而不是泛泛的「人物库是空的」。
 * 链接走 `/characters/new-real`(老板 IP 采集流,与 CharacterLibraryPage 同一入口)。
 */
function CastRealEmptyHint({ onCreate }: { onCreate: () => void }) {
  return (
    <>
      还没有真人人物,先去
      <button
        type="button"
        onClick={onCreate}
        className="mx-1 font-medium text-primary underline underline-offset-2 hover:no-underline"
      >
        『真人(老板 IP)』
      </button>
      建一个。
    </>
  );
}

/**
 * 视频「选人物(可选)」面板(packs W4)。常规手动流是主线:不选照旧(引擎自产主角);
 * 选了 → 主定妆图作各镜 i2i 锚 + appearance 逐字进提示词链(拼装在编排提交时,见 characterPick.ts)。
 */
export function CharacterPanel({
  form,
  disabled,
  onPick,
  onClear,
  compact,
  castReal,
  onNavigateToRealCreate,
}: {
  form: FormState;
  disabled?: boolean;
  onPick: (pick: CharacterPick) => void;
  onClear: () => void;
  /**
   * 紧凑形态(2026-08-06 重排 C4)—— 并进备料台③的「素材」行,与产品图同形。
   *
   * ★重排前这块是个**孤儿元素**:一个 `<Label>` + 一颗按钮挤在一行,夹在「产品图」和
   *   「风格与格式」两张大卡之间,既不是卡片也不在卡片里 —— 真机截图上看得很清楚。
   *   它和产品图本是同一件事(给这条片的"料"),就该长一个样、待在一起。
   */
  compact?: boolean;
  /**
   * SEEDANCE 真人出镜 P0(0914 车 D2):从「真人出镜」卡 / URL `?cast=real` 落地时为 true——
   * 面板默认展开(弹框自动打开,省一次「人物」按钮点击)、选择器默认只看真人人物、
   * 库里没有真人时空态指一条路去 `/characters/new-real` 建一个。
   */
  castReal?: boolean;
  /** castReal 空态的「去建一个」跳转;不传则不渲那条链接(向后兼容,老调用点不受影响)。 */
  onNavigateToRealCreate?: () => void;
}) {
  const [open, setOpen] = useState(!!castReal);
  const emptyHint =
    castReal && onNavigateToRealCreate ? (
      <CastRealEmptyHint onCreate={onNavigateToRealCreate} />
    ) : (
      PICKER_EMPTY_HINT
    );

  if (compact) {
    return (
      <>
        {form.characterId ? (
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/5 px-2.5 py-1.5 text-[12.5px]">
            <UserRound className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="max-w-[11rem] truncate">{form.characterName}</span>
            {form.protagonistAnchorKey ? (
              <Badge variant="secondary" className="shrink-0 text-2xs">
                定妆图锚定
              </Badge>
            ) : (
              /* 没有定妆图时只有文字锁 —— 说出来,别让人以为形象已经锁死了。 */
              <Badge variant="outline" className="shrink-0 text-2xs">
                仅文字锁
              </Badge>
            )}
            <button
              type="button"
              aria-label="清除人物"
              disabled={disabled}
              onClick={onClear}
              className="cursor-pointer rounded text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen(true)}
            data-testid="pick-character"
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-border/70 px-3 py-1.5 text-[12.5px] text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
          >
            <UserRound className="h-3.5 w-3.5" />
            人物
          </button>
        )}
        <CharacterPickerDialog
          open={open}
          onOpenChange={setOpen}
          onSelect={onPick}
          realOnly={castReal}
          emptyHint={emptyHint}
          gateRealPersonByVerification
        />
      </>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">主角人物(可选,来自人物库)</Label>
      {form.characterId ? (
        <div className="flex items-center gap-2 rounded-[calc(var(--radius)*1.5)] border bg-card px-3 py-2">
          <UserRound className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate text-sm">
            {form.characterName}
            <span className="ml-2 text-xs text-muted-foreground">
              {form.protagonistAnchorKey ? "主定妆图已锚定各镜首帧" : "该人物没有定妆图,仅文字锁"}
            </span>
          </span>
          {form.characterAppearance && (
            <Badge variant="secondary" className="hidden shrink-0 text-2xs sm:inline-flex">
              外观逐字锁
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            aria-label="清除人物"
            disabled={disabled}
            onClick={onClear}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => setOpen(true)}
          data-testid="pick-character"
        >
          <UserRound className="mr-2 h-3.5 w-3.5" />
          选人物
        </Button>
      )}
      <CharacterPickerDialog
        open={open}
        onOpenChange={setOpen}
        onSelect={onPick}
        realOnly={castReal}
        emptyHint={emptyHint}
        gateRealPersonByVerification
      />
    </div>
  );
}
